import { createStyles } from '@mantine/core';
import React from 'react';

import { Title } from '~/domains/design-system/Title';

export type TagProps = {
  children: React.ReactNode;
  className?: string;
  marginBottom?: number | string;
  variant?: 'default' | 'primary-stroke' | 'secondary-stroke';
  size?: 'small' | 'medium' | 'large';
  titleColor?: string;
  icon?: React.ReactNode;
  iconPosition?: 'left' | 'right';
};

const useStyles = createStyles((theme) => ({
  base: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    gap: theme.other.spacing._4,
    fontWeight: 900,
    whiteSpace: 'nowrap',
  },
  default: {
    borderRadius: /* eslint-disable-line local-rules/use-theme-tokens */ '100px',
    background: theme.colors.blue[2],
  },
  'primary-stroke': {
    borderRadius: /* eslint-disable-line local-rules/use-theme-tokens */ '12px',
    border: `1px solid ${theme.colors.blue[7]}`,
    background: 'transparent',
  },
  'secondary-stroke': {
    borderRadius: /* eslint-disable-line local-rules/use-theme-tokens */ '12px',
    border: `1px solid ${theme.colors.gray2[3]}`,
    background: 'transparent',
  },
  small: {
    padding: `0 ${theme.other.spacing._8}px`,
    height: '24px',
  },
  medium: {
    padding: `0 ${theme.other.spacing._12}px`,
    height: '32px',
  },
  large: {
    padding: `0 ${theme.other.spacing._16}px`,
    height: '40px',
  },
}));

export function Tag({
  children,
  className,
  marginBottom,
  variant = 'default',
  size = 'small',
  titleColor = 'gray2.6',
  icon,
  iconPosition = 'left',
}: TagProps) {
  const { classes, cx, theme } = useStyles();

  let textColor = titleColor;
  let iconColor: string;

  if (variant === 'primary-stroke') {
    textColor = 'blue.7';
    iconColor = theme.colors.blue[7];
  } else if (variant === 'secondary-stroke') {
    textColor = 'gray2.3';
    iconColor = theme.colors.gray2[3];
  } else {
    iconColor = theme.colors.gray2[6];
  }

  let titleVariant: 'title-small' | 'title-medium' | 'title-large' = 'title-small';
  if (size === 'medium') {
    titleVariant = 'title-medium';
  } else if (size === 'large') {
    titleVariant = 'title-large';
  }

  const content = (
    <>
      {icon &&
        iconPosition === 'left' &&
        React.cloneElement(icon as React.ReactElement, { color: iconColor })}
      <Title variant={titleVariant} c={textColor}>
        {children}
      </Title>
      {icon &&
        iconPosition === 'right' &&
        React.cloneElement(icon as React.ReactElement, { color: iconColor })}
    </>
  );

  return (
    <div style={{ display: 'flex', justifyContent: 'center', marginBottom }}>
      <div className={cx(classes.base, classes[variant], classes[size], className)}>{content}</div>
    </div>
  );
}

Tag.displayName = 'Tag';
